import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';

import { Box } from '@mui/material';
import type { CellRange } from 'ag-grid-community';

import Loading from 'src/components/shared/Loading';
import LoadingError from 'src/components/shared/LoadingError';
import NonIdealState from 'src/components/shared/NonIdealState';

import { ApiMergeResolutionDataSource } from '../api/ApiMergeResolutionDataSource';
import type { MergeResolutionDataSource } from '../api/MergeResolutionDataSource';
import type {
  MergeDecisionDto,
  MergeRowDto,
  MergeSection,
  TabPeriodSpanDto,
} from '../api/mergeResolution.types';
import { PAGE_SIZE, SUBMIT_ROUTE_ENABLED } from '../config';
import { useMergeSession } from '../hooks/useMergeSession';
import type { MergeSessionActions } from '../hooks/useMergeSession';
import type { ResolveContext } from '../hooks/useResolveContext';
import { useResolveContext } from '../hooks/useResolveContext';
import {
  resolutionUnavailableMessage,
  useSubmitFlow,
} from '../hooks/useSubmitFlow';
import { cellKey } from '../state/cellKey';
import { estimateCountsFromFacts } from '../state/estimateCounts';
import { submitGate } from '../state/gate';
import { inputTabs, sectionPagerLabel } from '../state/labels';
import { legalChoices } from '../state/legality';
import { activeTab, validSpans, visiblePeriodKeys } from '../state/periodRange';
import { rowDecisionStatus } from '../state/rowStatus';
import { cellsInRanges, expandCellRange } from '../state/selection';
import { cellTone } from '../state/tone';
import type { CellKey, PeriodRangeMode } from '../state/types';

import ChangedUnderneathBanner from './ChangedUnderneathBanner';
import ConflictGrid, { EXPAND_COLUMN_WIDTH } from './ConflictGrid';
import type {
  ConflictGridContext,
  ConflictGridHandle,
  DetailPane,
  GridLayout,
} from './ConflictGrid';
import { DecisionSwitchRenderer } from './DecisionSwitch';
import DecisionToolbar from './DecisionToolbar';
import FooterBar from './FooterBar';
import {
  PERIOD_COLUMN_WIDTH,
  buildColumnDefs,
  pinnedWidth,
} from './gridColumns';
import InputTabsRail from './InputTabsRail';
import NewConflictsDialog from './NewConflictsDialog';
import PeriodRangeToggle from './PeriodRangeToggle';
import ResolutionHeader from './ResolutionHeader';
import RowDetail from './RowDetail';
import SectionAccordion from './SectionAccordion';
import SectionPager from './SectionPager';
import StructuralConflictBanner from './StructuralConflictBanner';
import SubmitBlockerOverlay from './SubmitBlockerOverlay';

// Created once at module scope rather than as a default-parameter expression.
// `dataSource = new ApiMergeResolutionDataSource()` in the signature would
// construct a fresh instance on every render in which the caller omits the
// prop, and `useMergeSession`'s bootstrap effect depends on `ds` identity —
// a new instance each render would re-run the diff/rows bootstrap in
// a loop. A single shared default has the same externally-visible default
// behaviour (the prop is optional and falls back to a real API-backed
// source) without that churn.
const DEFAULT_DATA_SOURCE = new ApiMergeResolutionDataSource();

/** Any error carrying `.code === 'FORBIDDEN'`, whichever hook raised it. */
function isForbiddenError(err: unknown): boolean {
  return (
    typeof err === 'object' &&
    err !== null &&
    'code' in err &&
    (err as { code?: unknown }).code === 'FORBIDDEN'
  );
}

/**
 * Distinct DateIDs across a page's rows, in first-seen order. Not a business
 * rule — purely a display convenience for how many period columns a grid
 * needs, derived from whatever rows actually came back for that section.
 */
function periodKeysOf(rows: MergeRowDto[]): (string | null)[] {
  const seen = new Set<string>();
  const keys: (string | null)[] = [];
  for (const row of rows) {
    for (const period of row.Periods) {
      const token = period.DateID ?? '';
      if (!seen.has(token)) {
        seen.add(token);
        keys.push(period.DateID);
      }
    }
  }
  return keys;
}

interface ResolveConflictsScreenProps {
  dataSource?: MergeResolutionDataSource;
}

export const ResolveConflictsScreen = ({
  dataSource = DEFAULT_DATA_SOURCE,
}: ResolveConflictsScreenProps) => {
  const ctx = useResolveContext();

  switch (ctx.kind) {
    case 'invalid-params':
      return <Navigate to="/scenario-manager" replace />;

    case 'loading':
      return <Loading />;

    case 'not-found':
      return <NonIdealState title="Submission not found" />;

    case 'forbidden':
      return (
        <NonIdealState title="This model instance's layout is not supported" />
      );

    case 'error':
      return isForbiddenError(ctx.error) ? (
        <NonIdealState title="This model instance's layout is not supported" />
      ) : (
        <LoadingError retry={ctx.retry} />
      );

    case 'ready':
      return <ReadyResolveScreen ctx={ctx} dataSource={dataSource} />;
  }
};

export default ResolveConflictsScreen;

interface ReadyResolveScreenProps {
  ctx: Extract<ResolveContext, { kind: 'ready' }>;
  dataSource: MergeResolutionDataSource;
}

const ReadyResolveScreen = ({ ctx, dataSource }: ReadyResolveScreenProps) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const session = useMergeSession(dataSource, ctx.ids, ctx.session);
  const submitFlow = useSubmitFlow(dataSource, ctx.ids, session);

  const { state } = session;

  // D-Q11: the header's version labels read "?" until `setVersions` is
  // called. `baseSubmissionId`/`currentSubmissionId` are 0 until the diff
  // bootstrap lands (see `initialState`), and a rebase can move
  // `currentSubmissionId` again later — both are worth reflecting, so this
  // re-fires whenever either changes, guarded so an unchanged pair (most
  // renders) never calls `setVersions` again and churns `useResolveContext`.
  const lastVersionsRef = useRef<{ base: number; current: number } | null>(
    null
  );
  const { baseSubmissionId, currentSubmissionId } = state.session;
  useEffect(() => {
    if (baseSubmissionId === 0 && currentSubmissionId === 0) {
      return;
    }
    const last = lastVersionsRef.current;
    if (
      last !== null &&
      last.base === baseSubmissionId &&
      last.current === currentSubmissionId
    ) {
      return;
    }
    lastVersionsRef.current = {
      base: baseSubmissionId,
      current: currentSubmissionId,
    };
    ctx.setVersions(baseSubmissionId, currentSubmissionId);
    // `ctx.setVersions` is stable (useCallback with no deps in
    // useResolveContext); depending on it rather than the whole `ctx` object
    // avoids re-running this effect every time an unrelated field on `ctx`
    // changes identity.
  }, [baseSubmissionId, currentSubmissionId, ctx.setVersions]);

  // D-Q9: the dialog is driven by whichever hook most recently surfaced a
  // reopen, but neither hook exposes a way to clear `session.newConflicts`
  // (only `submitFlow.dismissNewConflicts` exists). Tracking the last
  // dismissed count locally is what lets "OK" close the dialog regardless of
  // which hook produced the number, without resurfacing it until a later
  // rebase changes the count again.
  const [dismissedConflictCount, setDismissedConflictCount] = useState<
    number | null
  >(null);
  const reopenedCount = submitFlow.newConflicts ?? session.newConflicts;
  const dialogOpen =
    reopenedCount !== null && reopenedCount !== dismissedConflictCount;
  const handleDismissNewConflicts = useCallback(() => {
    setDismissedConflictCount(reopenedCount);
    submitFlow.dismissNewConflicts();
  }, [reopenedCount, submitFlow]);

  // The server's counts at load, adjusted for every decision since, so they
  // move with each click without a round trip (DESIGN.md invariant 1).
  const displayCounts = useMemo(
    () =>
      state.serverCounts
        ? estimateCountsFromFacts(
            state.serverCounts,
            state.serverDecisionKeys,
            state.decisions,
            state.cellFacts,
            state.serverDecisions
          )
        : undefined,
    [
      state.serverCounts,
      state.serverDecisionKeys,
      state.decisions,
      state.cellFacts,
      state.serverDecisions,
    ]
  );

  // A courtesy: Submit rebases and checks the server's counts before it sends.
  const gate = useMemo(
    () =>
      submitGate(displayCounts, state.structural.blocked, SUBMIT_ROUTE_ENABLED),
    [displayCounts, state.structural.blocked]
  );

  const tabs = useMemo(
    () =>
      displayCounts
        ? inputTabs(
            displayCounts,
            state.pages,
            state.ui.section,
            state.decisions,
            state.staleResolved,
            state.ui.changedUnderneathOnly
          )
        : [],
    [
      displayCounts,
      state.pages,
      state.ui.section,
      state.decisions,
      state.staleResolved,
      state.ui.changedUnderneathOnly,
    ]
  );

  // The selected tab's own counts, for the second block in each section
  // header. Absent without ByInput: there is nothing per-tab to show.
  const selectedInputCounts = useMemo(() => {
    const item = displayCounts?.ByInput?.find(
      (i) => i.Input_Name === state.ui.inputKey
    );
    return item ? { name: item.Input_Name, counts: item } : undefined;
  }, [displayCounts, state.ui.inputKey]);

  // Whether "Show only these" would drop at least one tab from the rail.
  const toggleCouldFilterTabs = useMemo(
    () =>
      displayCounts?.ByInput?.some(
        (i) =>
          (i.TotalConflicts > 0 || i.AutoResolved > 0) &&
          !(i.TotalConflicts > 0 && i.ChangedUnderneath > 0)
      ) ?? false,
    [displayCounts]
  );

  // Validated once here (fail safe = show); memoised so the Map keeps its
  // identity and the panels' columnDefs stay stable between renders.
  const spans = useMemo(
    () => validSpans(state.periodSpans),
    [state.periodSpans]
  );
  const periodRange = state.ui.periodRange;
  const toggleTab = activeTab(
    state.ui.inputKey,
    state.pages[state.ui.section]?.rows ?? [],
    spans
  );
  const toggleSpan = toggleTab === null ? undefined : spans.get(toggleTab);
  const appBarOffset = useAppBarOffset();
  const periodToggle = (
    <PeriodRangeToggle
      span={toggleSpan}
      mode={periodRange}
      periodLabel={ctx.periodLabel}
      onChange={session.actions.setPeriodRange}
    />
  );

  const activeTabLabel =
    tabs.find((t) => t.key === state.ui.inputKey)?.label ??
    state.ui.inputKey ??
    'All inputs';

  // One tab in the rail is always selected, defaulting to the top one.
  //
  // `inputKey === null` means "no input filter", which mixes every input's rows
  // into one grid — and the dimension columns are per-input, so the headers
  // would describe one input while the rows came from several. Selecting the
  // first tab instead means the grid always shows a single input's dimension
  // set.
  //
  // The tab list is per-section and is rebuilt whenever the counts move, so the
  // guard is "is the current key still in the list", not "is this the first
  // render": switching section can leave a key selected that the new section
  // has no tab for.
  const { selectInput } = session.actions;
  const selectedTabKey = state.ui.inputKey;
  useEffect(() => {
    if (tabs.length === 0) {
      // Clear rather than keep a key this section has no tab for, which would
      // filter the grid down to no rows with no tab selected to explain why.
      if (selectedTabKey !== null) {
        selectInput(null);
      }
      return;
    }
    if (!tabs.some((t) => t.key === selectedTabKey)) {
      selectInput(tabs[0].key);
    }
  }, [tabs, selectedTabKey, selectInput]);

  // Curried on the layout numbers the panel owns: `ReadyResolveScreen` has the
  // data the detail needs, but only `SectionGridPanel` knows how wide that
  // section's frozen block and period columns are.
  const detail = useCallback(
    (
      row: MergeRowDto,
      pane: DetailPane,
      layout?: {
        periodKeys: (string | null)[];
        labelWidth: number;
        periodWidth: number;
      }
    ): ReactNode => (
      <RowDetail
        row={row}
        headers={ctx.dimensionHeaders}
        label={ctx.periodLabel}
        pane={pane}
        periodKeys={layout?.periodKeys}
        labelWidth={layout?.labelWidth}
        periodWidth={layout?.periodWidth ?? PERIOD_COLUMN_WIDTH}
        decisions={state.decisions}
        onCustom={(period, value) => {
          const result = session.actions.custom(row, period, value);
          if (!result.ok) {
            enqueueSnackbar(result.reason, { variant: 'error' });
          }
        }}
        onClear={(period) => {
          session.actions.clear(row, period);
        }}
        onRevert={(period) => {
          session.actions.revert(row, period);
        }}
        legal={legalChoices}
      />
    ),
    [
      ctx.dimensionHeaders,
      ctx.periodLabel,
      state.decisions,
      session.actions,
      enqueueSnackbar,
    ]
  );

  // Still bootstrapping the merge session itself (the diff), distinct
  // from `ctx.kind === 'loading'`, which only covers the five outer fetches
  // `useResolveContext` owns. Nothing below this point has real data yet.
  //
  // Checked only after the structural and error states. Neither of those ever
  // brings server counts: a structural diff stops before counts load, and the
  // session hook withholds counts while an error is set (fail closed). Testing
  // this first would therefore show a spinner that never ends.
  const bootstrapping = session.loading.diff || !state.serverCounts;

  const busy = session.rebasing || submitFlow.submitting;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      <ResolutionHeader
        scenarioName={ctx.labels.scenarioName}
        groupName={ctx.labels.groupName}
        baseSaved={ctx.labels.baseSaved}
        mySaved={ctx.labels.mySaved}
        currentSaved={ctx.labels.currentSaved}
      />

      {state.structural.blocked ? (
        // Terminal and not retryable: no decision can resolve a row-set
        // difference here, so there is no Retry and no grid.
        <Box component="main" sx={{ flex: 1, minWidth: 0, p: 2 }}>
          <StructuralConflictBanner message={state.structural.message} />
        </Box>
      ) : session.error ? (
        isForbiddenError(session.error) ? (
          <NonIdealState title="This model instance's layout is not supported" />
        ) : session.error.code === 'RESOLUTION_UNAVAILABLE' ? (
          // Another user's resolution, not a load failure: Retry would only
          // hit the same 409, so show the State-based message instead.
          <NonIdealState title={resolutionUnavailableMessage(session.error)} />
        ) : (
          <LoadingError retry={session.actions.retry} />
        )
      ) : bootstrapping ? (
        <Loading />
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
          }}
        >
          {/* Sticky: the title and sub-header scroll away, the tabs stay. */}
          <Box
            component="nav"
            sx={{
              flex: '0 0 auto',
              minWidth: 0,
              position: 'sticky',
              top: appBarOffset,
              zIndex: (t) => t.zIndex.appBar - 1,
              backgroundColor: 'background.paper',
            }}
          >
            <InputTabsRail
              tabs={tabs}
              selected={state.ui.inputKey}
              onSelect={session.actions.selectInput}
              loading={
                session.loading.rows ||
                state.pages[state.ui.section] === undefined
              }
            />
          </Box>

          <Box component="main" sx={{ flex: 1, minWidth: 0, p: 2 }}>
            {/* Nothing on this tab needs a decision: the "values changed while
                you were resolving" prompt has nothing to ask of the user,
                unless its toggle could still narrow the tab rail (or is
                already on), which the user must be able to reach from here. */}
            {(toggleSpan?.NeedsDecision !== null ||
              toggleCouldFilterTabs ||
              state.ui.changedUnderneathOnly) && (
              <ChangedUnderneathBanner
                count={displayCounts?.ChangedUnderneath ?? 0}
                active={state.ui.changedUnderneathOnly}
                onToggle={session.actions.toggleChangedUnderneath}
              />
            )}

            {(['needs_decision', 'auto_resolved'] as MergeSection[]).map(
              (section) => (
                <SectionAccordion
                  key={section}
                  section={section}
                  counts={displayCounts!}
                  inputCounts={selectedInputCounts}
                  note={
                    section === 'auto_resolved' &&
                    toggleSpan?.NeedsDecision === null
                      ? NO_ACTION_NOTE
                      : undefined
                  }
                  expanded={state.ui.section === section}
                  onToggle={() => session.actions.selectSection(section)}
                >
                  <SectionGridPanel
                    section={section}
                    rows={state.pages[section]?.rows ?? []}
                    loading={
                      state.ui.section === section &&
                      (session.loading.rows ||
                        state.pages[section] === undefined)
                    }
                    decisions={state.decisions}
                    staleResolved={state.staleResolved}
                    reopened={state.reopened}
                    dimensionHeaders={ctx.dimensionHeaders}
                    periodLabel={ctx.periodLabel}
                    spans={spans}
                    mode={periodRange}
                    inputLabel={activeTabLabel}
                    expandedRowId={state.ui.expandedRowId}
                    onExpand={session.actions.expand}
                    detail={detail}
                    applyDecisions={session.actions.applyDecisions}
                    onTakeRow={session.actions.takeRow}
                    onUndoRow={session.actions.undoRow}
                    decisionsSwitchDisabled={busy}
                    toolbarDisabled={busy || session.loading.rows}
                    // Only the expanded section carries it, so there is one
                    // toggle in the DOM, not one per section.
                    periodToggle={
                      state.ui.section === section ? periodToggle : null
                    }
                  />
                  <SectionPager
                    label={sectionPagerLabel(section)}
                    page={state.ui.requestedPages[section] ?? 1}
                    loadedPage={state.pages[section]?.page ?? 1}
                    rowsOnPage={state.pages[section]?.rows.length ?? 0}
                    totalRows={state.pages[section]?.totalRows ?? 0}
                    pageSize={PAGE_SIZE}
                    // Not disabled while rows load: that would disable the
                    // focused page button and drop keyboard focus to the
                    // body. A click mid-load is harmless - the rows fetch it
                    // starts supersedes the one in flight, whose response the
                    // hook's sequence guard then discards.
                    disabled={busy}
                    onChange={(page) => session.actions.setPage(section, page)}
                  />
                </SectionAccordion>
              )
            )}
          </Box>
        </Box>
      )}

      <FooterBar
        gate={gate}
        counts={displayCounts ?? state.serverCounts}
        submitting={submitFlow.submitting}
        onCancel={() => navigate(-1)}
        onSubmit={submitFlow.submit}
      />

      <SubmitBlockerOverlay open={submitFlow.submitting} />

      <NewConflictsDialog
        open={dialogOpen}
        reopenedCount={reopenedCount ?? 0}
        onClose={handleDismissNewConflicts}
      />

      {/* PHASE2_DOWNSTREAM_IMPACT is false: no Downstream Impact panel, no
          acknowledgment panel, renders nothing further. */}
    </Box>
  );
};

/**
 * Roughly the width of the toolbar's three buttons. The indent below is capped
 * so that much of the container always remains, which keeps the buttons
 * reachable on a viewport narrower than the frozen block. `min()` going
 * negative is floored at zero by CSS, so the indent simply disappears rather
 * than breaking the rule.
 */
const TOOLBAR_MIN_VISIBLE_PX = 320;

/**
 * Shown at the right of the Auto Resolved header when the active tab has no row
 * to decide: those rows are informational.
 */
const NO_ACTION_NOTE = [
  'No action is needed on this tab.',
  'These rows are for awareness only, since they reflect changes to your shared base that could affect your inputs.',
];

/**
 * Height of the app's fixed top bar, measured rather than assumed: the tabs
 * stick just below it, and the bar's height depends on the breakpoint.
 */
const useAppBarOffset = (): number => {
  const [offset, setOffset] = useState(0);
  useLayoutEffect(() => {
    const measure = () => {
      // Attribute match, not `.MuiAppBar-root`: the app remaps MUI's class
      // prefix to `Trinity` (src/MuiClassNameSetup.ts). The header's bar comes
      // first in the DOM; the copyright bar is a second AppBar after it.
      const bar = document.querySelector('[class*="AppBar-root"]');
      setOffset(bar ? Math.round(bar.getBoundingClientRect().height) : 0);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);
  return offset;
};

/**
 * Accessible name for each section's colour legend. The legend is rendered
 * once per section now, so the two need telling apart by name.
 */
const LEGEND_LABEL: Record<MergeSection, string> = {
  needs_decision: 'Colour legend for rows needing a decision',
  auto_resolved: 'Colour legend for auto resolved rows',
};

interface SectionGridPanelProps {
  section: MergeSection;
  rows: MergeRowDto[];
  loading: boolean;
  decisions: Record<CellKey, MergeDecisionDto>;
  staleResolved: Record<CellKey, true>;
  reopened: CellKey[];
  dimensionHeaders: string[];
  periodLabel: (dateId: string | null) => string;
  spans: Map<string, TabPeriodSpanDto>;
  mode: PeriodRangeMode;
  inputLabel: string;
  expandedRowId: string | null;
  onExpand: (rowId: string | null) => void;
  detail: (
    row: MergeRowDto,
    pane: DetailPane,
    layout?: {
      periodKeys: (string | null)[];
      labelWidth: number;
      periodWidth: number;
    }
  ) => ReactNode;
  applyDecisions: MergeSessionActions['applyDecisions'];
  onTakeRow: MergeSessionActions['takeRow'];
  onUndoRow: MergeSessionActions['undoRow'];
  decisionsSwitchDisabled: boolean;
  toolbarDisabled: boolean;
  /** The period-range switch, shown in the toolbar row beside the legend. */
  periodToggle: ReactNode;
}

const SectionGridPanel = ({
  section,
  rows,
  loading,
  decisions,
  staleResolved,
  reopened,
  dimensionHeaders,
  periodLabel,
  spans,
  mode,
  inputLabel,
  expandedRowId,
  onExpand,
  detail,
  applyDecisions,
  onTakeRow,
  onUndoRow,
  decisionsSwitchDisabled,
  toolbarDisabled,
  periodToggle,
}: SectionGridPanelProps) => {
  const gridRef = useRef<ConflictGridHandle>(null);
  const [ranges, setRanges] = useState<CellRange[]>([]);

  // Visible columns only: feeds the grid, range selection and the detail (and
  // so the clipboard). The row-level take is unaffected (J4).
  const periodKeys = useMemo(
    () => visiblePeriodKeys(periodKeysOf(rows), rows, spans, mode),
    [rows, spans, mode]
  );

  const rowMap = useMemo(
    () => new Map(rows.map((row) => [row.Input_RowID, row])),
    [rows]
  );

  const statusFor = useCallback(
    (rowId: string) => {
      const row = rowMap.get(rowId);
      return row ? rowDecisionStatus(row, decisions) : 'PENDING';
    },
    [rowMap, decisions]
  );

  const reopenedSet = useMemo(() => new Set(reopened), [reopened]);

  // Memoized on decision/reopened/row identity only — not on `ranges` — so a
  // drag in progress never changes `columnDefs` identity. `ConflictGrid`
  // force-refreshes cells whenever `columnDefs` changes, which the comment
  // there warns must not happen mid-drag.
  // An API that predates `Input_Name` sends none, and then the column would be empty.
  const showInputName = useMemo(
    () => rows.some((row) => !!row.Input_Name),
    [rows]
  );

  const columnDefs = useMemo(() => {
    const periodOf = (rowId: string, dateId: string | null) =>
      rowMap.get(rowId)?.Periods.find((p) => p.DateID === dateId);

    const toneFor = (rowId: string, dateId: string | null) => {
      const period = periodOf(rowId, dateId);
      if (!period) {
        return { tone: 'grey' as const, resolvedThisSession: false };
      }
      return cellTone(period, decisions[cellKey(rowId, dateId)]);
    };

    const decisionValueFor = (
      rowId: string,
      dateId: string | null
    ): string | null | undefined => {
      const decision = decisions[cellKey(rowId, dateId)];
      if (!decision) {
        return undefined;
      }
      if (decision.choice === 'CLEAR') {
        return null;
      }
      if (decision.choice === 'CUSTOM') {
        return decision.custom_value ?? null;
      }
      const period = periodOf(rowId, dateId);
      if (!period) {
        return undefined;
      }
      return decision.choice === 'MINE' ? period.Mine : period.Theirs;
    };

    return buildColumnDefs({
      dimensionHeaders,
      periodKeys,
      label: periodLabel,
      toneFor,
      statusFor,
      decisionValueFor,
      resolvedIsStale: (rowId, dateId) =>
        cellKey(rowId, dateId) in staleResolved,
      reopened: reopenedSet,
      decisionSwitchRenderer: DecisionSwitchRenderer,
      showInputName,
    });
  }, [
    showInputName,
    rowMap,
    decisions,
    staleResolved,
    reopenedSet,
    dimensionHeaders,
    periodKeys,
    periodLabel,
    statusFor,
  ]);

  // A range's row positions are in the grid's displayed model (filtered and
  // sorted), not in `rows`, so they are resolved through the grid.
  const rowAt = useCallback(
    (index: number) => gridRef.current?.rowAtDisplayedIndex(index),
    []
  );

  const selectedCellCount = useMemo(
    () => cellsInRanges(rows, ranges, periodKeys, rowAt).length,
    [rows, ranges, periodKeys, rowAt]
  );

  // The toolbar acts on selected *period* cells, so it is aligned with where
  // those cells begin — the freeze boundary — rather than with the left edge
  // of the frozen identity block. It is deliberately not inside the grid, so
  // it holds that position while the periods scroll underneath it.
  // The grid's column geometry. The colDef widths are the right answer only
  // until the grid autofits its columns to their contents, after which the
  // grid reports the measured geometry back and that becomes authoritative.
  // The computed values are the fallback for the first render, before the grid
  // is ready.
  const [measured, setMeasured] = useState<GridLayout | null>(null);
  const declaredFrozenWidth = useMemo(
    () => EXPAND_COLUMN_WIDTH + pinnedWidth(columnDefs),
    [columnDefs]
  );
  const frozenWidth = measured?.frozenWidth ?? declaredFrozenWidth;
  const periodWidth = measured?.periodWidth ?? PERIOD_COLUMN_WIDTH;

  // Guarded so a report of unchanged geometry cannot loop through a re-render.
  const handleLayoutMeasured = useCallback((layout: GridLayout) => {
    setMeasured((current) =>
      current !== null &&
      current.frozenWidth === layout.frozenWidth &&
      current.periodWidth === layout.periodWidth
        ? current
        : layout
    );
  }, []);

  const handleTake = useCallback(
    (choice: 'MINE' | 'THEIRS') => {
      const toApply = expandCellRange(
        rows,
        ranges,
        periodKeys,
        section,
        choice,
        rowAt
      );
      // `applyDecisions` upserts the decisions locally; the counts and the
      // Submit gate follow from them, and the server is next asked at a
      // section/input/page change and at Submit.
      applyDecisions(toApply);
    },
    [rows, ranges, periodKeys, section, applyDecisions, rowAt]
  );

  const handleDeselect = useCallback(() => {
    gridRef.current?.clearSelection();
  }, []);

  // `frozenWidth` is the freeze boundary, which is exactly where the detail
  // panel's first value cell has to start, so the metric-label column is made
  // that wide.
  const detailForRow = useCallback(
    (row: MergeRowDto, pane: DetailPane) =>
      detail(row, pane, {
        periodKeys,
        labelWidth: frozenWidth,
        periodWidth,
      }),
    [detail, periodKeys, frozenWidth, periodWidth]
  );

  // The section comes from this panel's own prop, never from `ui.section`:
  // both panels' grids are mounted, so the switch in the collapsed one must
  // act on its own rows under its own section rule.
  const gridContext: ConflictGridContext = useMemo(
    () => ({
      statusFor,
      onTake: (rowId: string, choice: 'MINE' | 'THEIRS') =>
        onTakeRow(rowId, section, choice),
      onUndo: (rowId: string) => onUndoRow(rowId, section),
      disabled: decisionsSwitchDisabled,
    }),
    [statusFor, onTakeRow, onUndoRow, section, decisionsSwitchDisabled]
  );

  return (
    <Box>
      <Box>
        <DecisionToolbar
          // The buttons start at the freeze boundary, so they line up with the
          // period cells; the period toggle sits in the space to their left.
          leadingWidth={`min(${frozenWidth}px, calc(100% - ${TOOLBAR_MIN_VISIBLE_PX}px))`}
          selectedCellCount={selectedCellCount}
          // Any range at all, including one over the dimension columns, which
          // carries no decision but is still the user's to clear.
          hasSelection={ranges.length > 0}
          legendLabel={LEGEND_LABEL[section]}
          // Auto resolved rows are never a conflict, and the only way one is
          // decided is by overriding it. No Conflict stays (BOTH_SAME is grey),
          // under Overridden.
          legendOmit={section === 'auto_resolved' ? ['swatch-red'] : undefined}
          legendPlace={
            section === 'auto_resolved'
              ? { 'swatch-grey': { column: 3, row: 2 } }
              : undefined
          }
          legendRelabel={
            section === 'auto_resolved'
              ? { 'swatch-resolved': 'Overridden' }
              : undefined
          }
          disabled={toolbarDisabled}
          onTake={handleTake}
          onDeselect={handleDeselect}
          extra={periodToggle}
        />
      </Box>
      <ConflictGrid
        ref={gridRef}
        section={section}
        inputLabel={inputLabel}
        rows={rows}
        columnDefs={columnDefs}
        expandedRowId={expandedRowId}
        onExpand={onExpand}
        onRangeChange={setRanges}
        detail={detailForRow}
        onLayoutMeasured={handleLayoutMeasured}
        loading={loading}
        context={gridContext}
      />
    </Box>
  );
};
